import updateUserLanguage from "@casino/actions/updateUserLanguage";
import usePathHistory from "@casino/client/hooks/router/usePathHistory";
import inAYear from "@casino/shared/utils/date/inAYear";
import nativeRedirect from "@casino/shared/utils/router/nativeRedirect";
import { getCookie, setCookie } from "@workspace/cookies/browser";
import { LANGUAGE_COOKIE } from "@workspace/nextjs_utils/src/cookies/LANGUAGE_COOKIE";
import { usePathname } from "next/navigation";
import { useCallback } from "react";

type Props = {
  isProtoEnabled?: boolean;
  isLoggedIn: boolean;
  defaultLang: string;
  languages: LanguageCode[];
  languageFeatureFlags: Partial<Record<LanguageCode, boolean>>;
};

const useLanguageSwitcher = ({
  isLoggedIn,
  defaultLang,
  languages,
  languageFeatureFlags,
}: Props) => {
  const { getPreviousPath } = usePathHistory();
  const pathname = usePathname();
  const prevPathname = pathname?.includes("menu")
    ? getPreviousPath()
    : pathname;
  const enabledLanguages = languages.filter(
    (lang) => languageFeatureFlags[lang] !== false,
  );
  const userPreferenceId = getCookie("userPreferenceId");

  const updateLang = async (value: string | Language) => {
    await updateUserLanguage({
      language: value as string,
      userPreferenceId,
    });
  };

  const inAYearMemo = useCallback(() => inAYear(), []);

  const handleChange = async (value: string) => {
    const locale =
      enabledLanguages.find((lang) => lang === value) ?? defaultLang;

    if (isLoggedIn) {
      await updateLang(locale);
    }

    setCookie(LANGUAGE_COOKIE, locale, { expires: inAYearMemo() });
    nativeRedirect(prevPathname ?? "/", locale, defaultLang);
  };

  return { handleChange, languages: enabledLanguages };
};

export default useLanguageSwitcher;
